import { useSyncExternalStore } from 'react';

export type CookieConsent = { statistics: boolean; marketing: boolean };

// _document.tsx reads the same key to set Google's consent defaults before any tag loads.
const COOKIE_CONSENT_STORAGE_KEY = 'cookie-consent';

const listeners = new Set<() => void>();
let current: CookieConsent | null | undefined;

export function getCookieConsent() {
  if (current === undefined) {
    try {
      current = JSON.parse(localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) ?? 'null');
    } catch {
      current = null;
    }
  }

  return current ?? null;
}

export function saveCookieConsent(consent: CookieConsent) {
  current = consent;

  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Keep the choice for this visit even if storage is unavailable.
  }

  const marketing = consent.marketing ? 'granted' : 'denied';
  window.gtag?.('consent', 'update', {
    analytics_storage: consent.statistics ? 'granted' : 'denied',
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  });
  // The pixels load only after marketing consent; stop them if it is withdrawn later.
  window.fbq?.('consent', consent.marketing ? 'grant' : 'revoke');
  window.oaiq?.('consent', consent.marketing);

  listeners.forEach((listener) => listener());
}

const openListeners = new Set<() => void>();

export function openCookieSettings() {
  openListeners.forEach((listener) => listener());
}

export function onOpenCookieSettings(listener: () => void) {
  openListeners.add(listener);
  return () => {
    openListeners.delete(listener);
  };
}

/** `undefined` during server render, `null` until the visitor has chosen. */
export function useCookieConsent() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getCookieConsent,
    () => undefined,
  );
}
